// src/admin/AdminHostelRooms.js
import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { v4 as uuidv4 } from 'uuid';

const ROOM_TYPES = ['dorm', 'private', 'ensuite'];
const GENDER_OPTIONS = ['mixed', 'female', 'male'];
const COMMON_AMENITIES = [
  'AC', 'Heating', 'Private Locker', 'Reading Light',
  'Privacy Curtain', 'Balcony', 'Ensuite Bathroom',
  'Shared Bathroom', 'Free Towel', 'Kitchen', 'Paid Towel', 'Family Meals','Bar','Free Drink', 'Events'
];

function AdminHostelRooms({ hostel, onUpdate }) {
  const [rooms, setRooms] = useState([]);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [availabilityDate, setAvailabilityDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (hostel?.rooms) {
      setRooms(hostel.rooms);
    }
  }, [hostel]);

  const handleSaveRooms = async () => {
    try {
      const hostelRef = doc(db, 'hostels', hostel.id);
      
      // Calculate summary fields
      const totalBeds = rooms.reduce((sum, room) => sum + (room.totalBeds || 0), 0);
      const totalRooms = rooms.length;
      const hasGenderSpecificRooms = rooms.some(r => r.gender !== 'mixed');
      const hasMixedRooms = rooms.some(r => r.gender === 'mixed');
      
      await updateDoc(hostelRef, {
        rooms,
        totalBeds,
        totalRooms,
        hasGenderSpecificRooms,
        hasMixedRooms,
        availabilityLastUpdated: new Date().toISOString()
      });
      
      alert('Rooms saved successfully!');
      if (onUpdate) {
        onUpdate({ ...hostel, rooms, totalBeds, totalRooms });
      }
    } catch (error) {
      console.error('Error saving rooms:', error);
      alert('Failed to save rooms: ' + error.message);
    }
  };

  const handleAddRoom = () => {
    const newRoom = {
      id: uuidv4(),
      roomName: '',
      roomType: 'dorm',
      totalBeds: 4,
      gender: 'mixed',
      pricePerBed: 20,
      currency: hostel.currency || 'EUR',
      amenities: [],
      availability: {},
      floor: 1,
      photos: [],
      description: ''
    };
    setEditingRoom(newRoom);
    setShowRoomForm(true);
  };

  const handleEditRoom = (room) => {
    setEditingRoom({ ...room });
    setShowRoomForm(true);
  };

  const handleDeleteRoom = async (roomId) => {
    if (window.confirm('Are you sure you want to delete this room? This will also delete all room photos.')) {
      const roomToDelete = rooms.find(r => r.id === roomId);
      
      // Delete room photos from storage
      if (roomToDelete?.photos && roomToDelete.photos.length > 0) {
        for (const photoUrl of roomToDelete.photos) {
          try {
            const photoRef = ref(storage, photoUrl);
            await deleteObject(photoRef);
          } catch (error) {
            console.warn('Failed to delete room photo:', error);
          }
        }
      }
      
      setRooms(rooms.filter(r => r.id !== roomId));
    }
  };

  const handleSaveRoom = () => {
    if (!editingRoom.roomName) {
      alert('Room name is required');
      return;
    }

    if (rooms.find(r => r.id === editingRoom.id)) {
      // Update existing room
      setRooms(rooms.map(r => r.id === editingRoom.id ? editingRoom : r));
    } else {
      // Add new room
      setRooms([...rooms, editingRoom]);
    }
    
    setShowRoomForm(false);
    setEditingRoom(null);
  };

  const handleUpdateAvailability = (roomId, date, availableBeds, notes = '') => {
    setRooms(rooms.map(room => {
      if (room.id === roomId) {
        const bookedBeds = room.totalBeds - availableBeds;
        return {
          ...room,
          availability: {
            ...room.availability,
            [date]: { availableBeds, bookedBeds, notes }
          }
        };
      }
      return room;
    }));
  };

  const handleUploadRoomPhoto = async (file) => {
    if (!file || !editingRoom) return;
    
    setUploadingPhoto(true);
    try {
      const photoPath = `roomPhotos/${hostel.id}/${editingRoom.id}/${uuidv4()}_${file.name}`;
      const photoRef = ref(storage, photoPath);
      await uploadBytes(photoRef, file);
      const downloadUrl = await getDownloadURL(photoRef);
      
      const updatedPhotos = [...(editingRoom.photos || []), downloadUrl];
      setEditingRoom({ ...editingRoom, photos: updatedPhotos });
      alert('Photo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading room photo:', error);
      alert('Failed to upload photo: ' + error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteRoomPhoto = async (photoUrl) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      const photoRef = ref(storage, photoUrl);
      await deleteObject(photoRef);
      
      const updatedPhotos = editingRoom.photos.filter(p => p !== photoUrl);
      setEditingRoom({ ...editingRoom, photos: updatedPhotos });
      alert('Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting room photo:', error);
      alert('Failed to delete photo: ' + error.message);
    }
  };

  return (
    <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px' }}>
      <h3>Room Management for {hostel.name}</h3>
      
      {/* Summary Stats */}
      <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <strong>Summary:</strong> {rooms.length} rooms, {rooms.reduce((sum, r) => sum + r.totalBeds, 0)} total beds
        {hostel.hasGenderSpecificRooms && <span> • Has gender-specific rooms</span>}
        {hostel.hasMixedRooms && <span> • Has mixed rooms</span>}
        {hostel.cashRequired && <span> • 💵 Cash payment required</span>}
      </div>

      {/* Room List */}
      <div style={{ marginBottom: '20px' }}>
        <h4>Rooms</h4>
        {rooms.length === 0 ? (
          <p style={{ color: 'gray' }}>No rooms configured yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Room Name</th>
                <th style={{ padding: '8px' }}>Type</th>
                <th style={{ padding: '8px' }}>Beds</th>
                <th style={{ padding: '8px' }}>Gender</th>
                <th style={{ padding: '8px' }}>Price/Bed</th>
                <th style={{ padding: '8px' }}>Photos</th>
                <th style={{ padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map(room => (
                <tr key={room.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px' }}>{room.roomName}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{room.roomType}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{room.totalBeds}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: room.gender === 'female' ? '#ffebee' : 
                                     room.gender === 'male' ? '#e3f2fd' : '#f5f5f5',
                      fontSize: '12px'
                    }}>
                      {room.gender}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    {room.currency} {room.pricePerBed}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    {room.photos?.length || 0}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <button 
                      onClick={() => handleEditRoom(room)}
                      style={{ marginRight: '5px', padding: '4px 8px' }}
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteRoom(room.id)}
                      style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Availability Section */}
      <div style={{ marginBottom: '20px' }}>
        <h4>Availability for {availabilityDate}</h4>
        <input 
          type="date" 
          value={availabilityDate}
          onChange={(e) => setAvailabilityDate(e.target.value)}
          style={{ marginBottom: '10px' }}
        />
        
        {rooms.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Room</th>
                <th style={{ padding: '8px' }}>Total Beds</th>
                <th style={{ padding: '8px' }}>Available</th>
                <th style={{ padding: '8px' }}>Booked</th>
                <th style={{ padding: '8px' }}>Notes</th>
                <th style={{ padding: '8px' }}>Update</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map(room => {
                const availability = room.availability?.[availabilityDate] || 
                  { availableBeds: room.totalBeds, bookedBeds: 0, notes: '' };
                
                return (
                  <tr key={room.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{room.roomName}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{room.totalBeds}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <input
                        type="number"
                        min="0"
                        max={room.totalBeds}
                        value={availability.availableBeds}
                        onChange={(e) => {
                          const available = parseInt(e.target.value) || 0;
                          handleUpdateAvailability(room.id, availabilityDate, available, availability.notes);
                        }}
                        style={{ width: '60px' }}
                      />
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      {availability.bookedBeds}
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="text"
                        value={availability.notes || ''}
                        onChange={(e) => {
                          handleUpdateAvailability(room.id, availabilityDate, availability.availableBeds, e.target.value);
                        }}
                        placeholder="Notes..."
                        style={{ width: '100%' }}
                      />
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <span style={{ 
                        color: availability.availableBeds === 0 ? 'red' : 'green',
                        fontWeight: 'bold'
                      }}>
                        {availability.availableBeds === 0 ? 'FULL' : 'Available'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Action Buttons - CENTERED */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
        <button 
          onClick={handleAddRoom}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Add Room
        </button>
        <button 
          onClick={handleSaveRooms}
          style={{
            padding: '10px 20px',
            backgroundColor: '#853a38',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Save All Changes
        </button>
      </div>

      {/* Room Form Modal */}
      {showRoomForm && editingRoom && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '85vh',
            overflowY: 'auto'
          }}>
            <h3>{editingRoom.id ? 'Edit Room' : 'Add New Room'}</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Room Name*</label>
              <input
                type="text"
                value={editingRoom.roomName}
                onChange={(e) => setEditingRoom({ ...editingRoom, roomName: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
                placeholder="e.g., Ocean View 6-Bed Dorm"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Room Type</label>
                <select
                  value={editingRoom.roomType}
                  onChange={(e) => setEditingRoom({ ...editingRoom, roomType: e.target.value })}
                  style={{ width: '100%', padding: '8px' }}
                >
                  {ROOM_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Gender</label>
                <select
                  value={editingRoom.gender}
                  onChange={(e) => setEditingRoom({ ...editingRoom, gender: e.target.value })}
                  style={{ width: '100%', padding: '8px' }}
                >
                  {GENDER_OPTIONS.map(gender => (
                    <option key={gender} value={gender}>{gender}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Total Beds</label>
                <input
                  type="number"
                  min="1"
                  value={editingRoom.totalBeds}
                  onChange={(e) => setEditingRoom({ ...editingRoom, totalBeds: parseInt(e.target.value) || 1 })}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Price per Bed</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingRoom.pricePerBed}
                  onChange={(e) => setEditingRoom({ ...editingRoom, pricePerBed: parseFloat(e.target.value) || 0 })}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Currency</label>
                <select
                  value={editingRoom.currency}
                  onChange={(e) => setEditingRoom({ ...editingRoom, currency: e.target.value })}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Floor</label>
              <input
                type="number"
                value={editingRoom.floor}
                onChange={(e) => setEditingRoom({ ...editingRoom, floor: parseInt(e.target.value) || 1 })}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Description</label>
              <textarea
                value={editingRoom.description}
                onChange={(e) => setEditingRoom({ ...editingRoom, description: e.target.value })}
                style={{ width: '100%', padding: '8px', minHeight: '80px' }}
                placeholder="Room description..."
              />
            </div>

            {/* Room Photos Section */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Room Photos</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleUploadRoomPhoto(e.target.files[0])}
                disabled={uploadingPhoto}
                style={{ marginBottom: '10px' }}
              />
              {uploadingPhoto && <span style={{ color: 'blue' }}>Uploading...</span>}
              
              {editingRoom.photos && editingRoom.photos.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                  {editingRoom.photos.map((photo, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img 
                        src={photo} 
                        alt={`Room ${index + 1}`}
                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                      <button
                        onClick={() => handleDeleteRoomPhoto(photo)}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          backgroundColor: 'red',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Amenities</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {COMMON_AMENITIES.map(amenity => (
                  <label key={amenity} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="checkbox"
                      checked={editingRoom.amenities?.includes(amenity) || false}
                      onChange={(e) => {
                        const amenities = editingRoom.amenities || [];
                        if (e.target.checked) {
                          setEditingRoom({ ...editingRoom, amenities: [...amenities, amenity] });
                        } else {
                          setEditingRoom({ ...editingRoom, amenities: amenities.filter(a => a !== amenity) });
                        }
                      }}
                    />
                    {amenity}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRoomForm(false);
                  setEditingRoom(null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRoom}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#853a38',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Save Room
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminHostelRooms;